import React, { useState, useEffect } from "react";
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Box,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { getAuth, signInAnonymously } from "firebase/auth";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "../../../../firebaseConfig";

import { DeleteOutlined, ImageOutlined } from "@mui/icons-material";
import moment from "moment";

const ClinicForm = ({ countryList = [], clinic_id }) => {
  const [clinicDetails, setClinicDetails] = useState({
    name: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state_id: "",
    postcode: "",
    country_id: "",
    image_url: "",
  });
  const [stateList, setStateList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    signInAnonymously(auth)
      .then(() => {
        console.log("SIGNED IN");
      })
      .catch((error) => {
        console.error("Firebase sign-in error:", error);
      });
  }, []);

  const headers = {
    "X-API-Key": process.env.REACT_APP_EXPERT_API_KEY,
    "Content-Type": "application/json",
  };

  // Helper function for API calls
  const fetchAPI = async (url) => {
    try {
      const response = await fetch(url, { method: "GET", headers });
      if (!response.ok) throw new Error("Network response was not ok");
      return await response.json();
    } catch (error) {
      console.error(error);
      return { data: {} }; // Prevent crashing in case of error
    }
  };

  // Fetch states when country is selected
  useEffect(() => {
    const fetchStates = async () => {
      try {
        if (!clinicDetails.country_id) return;

        const countryItem = countryList.find(
          (item) => item.id === clinicDetails.country_id
        );

        if (!countryItem) return;

        const countryCode = countryItem?.code;
        if (!countryCode) return;

        const data = await fetchAPI(
          `https://us.uwc.world/new-uwc/state/${countryCode}`
        );

        if (data?.data?.states) {
          setStateList(data.data.states);
        } else {
          setStateList([]);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };

    fetchStates();
  }, [clinicDetails.country_id, countryList]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClinicDetails({
      ...clinicDetails,
      [name]: value,
    });
  };

  // Submit form
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const response = await fetch(
        "https://us.uwc.world/new-uwc/uwc_expert/copilot/clinic/create",
        {
          method: "POST",
          headers,
          body: JSON.stringify(clinicDetails),
        }
      );

      if (response.ok) {
        alert("Clinic created successfully");
      } else {
        alert("Failed to create clinic");
      }
    } catch (error) {
      alert("Error creating clinic. Please try again.");
      console.error("Submit error:", error);
    }

    setIsSubmitting(false);
  };

  const handleChangeProfilePic = async (event) => {
    try {
      const file = event.target.files[0];

      // Check if file type is valid
      const allowedTypes = ["image/png", "image/jpg", "image/jpeg"];
      if (!allowedTypes.includes(file?.type)) {
        console.error("Incorrect file format.");
        alert("Please upload a valid image file (png, jpg, jpeg).");
        return;
      }

      setUploading(true); // Show the uploading indicator

      // Create the path in Firebase Storage where the image will be saved
      const dirName = moment().format("MMMM_YYYY");
      const storageRef = ref(storage, `${dirName}/${file.name}`);

      // Start the upload task
      const uploadTask = uploadBytesResumable(storageRef, file);

      // Handle progress of the upload
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          // Optionally, track progress here if needed
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log("Upload is " + progress + "% done");
        },
        (error) => {
          // Handle upload error
          console.error("Upload error:", error);
          alert("Upload failed. Please try again.");
          setUploading(false);
        },
        async () => {
          try {
            // Get the download URL after successful upload
            const url = await getDownloadURL(uploadTask.snapshot.ref());
            console.log("Download URL:", url);

            // Update clinicDetails with the image URL
            setClinicDetails((prevDetails) => ({
              ...prevDetails,
              image_url: url, // Store the image URL in the state
            }));

            setUploading(false); // Hide the uploading indicator
          } catch (error) {
            console.error("Error getting download URL:", error);
            setUploading(false);
          }
        }
      );
    } catch (error) {
      console.error("Error handling file upload:", error);
      setUploading(false);
    }
  };

  const handleDeleteProfilePic = () => {
    setClinicDetails({
      ...clinicDetails,
      image_url: "",
    });
  };

  return (
    <Box>
      <h2>Create Clinic</h2>

      <div className="mt-4 d-flex align-items-center gap-3">
        <div className="position-relative">
          {uploading ? (
            <Skeleton
              sx={{ width: "120px", height: "120px", borderRadius: "20px" }}
              variant="rectangular"
            />
          ) : (
            <img
              src={clinicDetails?.image_url}
              width={120}
              height={120}
              alt="Profile"
              loading="lazy"
              style={{
                borderRadius: "20px",
                objectFit: "cover",
                backgroundColor: "rgba(0,0,0,0.07)",
              }}
            />
          )}
          {uploading && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <CircularProgress />
            </div>
          )}
        </div>
        <div>
          <Button
            component="label"
            htmlFor="profile-pic-upload"
            id="change_profile_pic"
            className="change_profile_pic"
            sx={{
              backgroundColor: "#F5F5F5",
              color: "#414141",
              borderRadius: "5px",
              padding: "10px 20px",
              textTransform: "capitalize",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              width: "200px",
            }}
          >
            <input
              id="profile-pic-upload"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleChangeProfilePic}
            />
            <ImageOutlined />
            Change Profile Pic
          </Button>
          <Button
            onClick={handleDeleteProfilePic}
            sx={{
              backgroundColor: "#D2293D1A",
              color: "#D2293D",
              borderRadius: "5px",
              padding: "10px 20px",
              textTransform: "capitalize",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              width: "200px",
              marginTop: "20px",
            }}
            id="delete_profile_click"
            className="delete_profile_click"
          >
            <DeleteOutlined />
            Delete Profile Pic
          </Button>
        </div>
      </div>
      <div>
        <Grid className="mt-3" container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Clinic Name"
              variant="outlined"
              fullWidth
              name="name"
              value={clinicDetails.name}
              onChange={handleInputChange}
              required
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Address Line 1"
              variant="outlined"
              fullWidth
              name="address_line_1"
              value={clinicDetails.address_line_1}
              onChange={handleInputChange}
              required
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Address Line 2"
              variant="outlined"
              fullWidth
              name="address_line_2"
              value={clinicDetails.address_line_2}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="City"
              variant="outlined"
              fullWidth
              name="city"
              value={clinicDetails.city}
              onChange={handleInputChange}
              required
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required>
              <InputLabel>State</InputLabel>
              <Select
                label="State"
                name="state_id"
                value={clinicDetails.state_id}
                onChange={handleInputChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {stateList.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Postcode"
              variant="outlined"
              fullWidth
              name="postcode"
              value={clinicDetails.postcode}
              onChange={handleInputChange}
              required
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required>
              <InputLabel>Country</InputLabel>
              <Select
                label="Country"
                name="country_id"
                value={clinicDetails.country_id}
                onChange={handleInputChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {countryList.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disableElevation
              disabled={isSubmitting}
              className="PrimaryCTA update_profile"
              id="update_profile"
              sx={{ marginLeft: "auto", display: "block", marginTop: "30px" }}
            >
              {isSubmitting ? <CircularProgress size={24} /> : "Create Clinic"}
            </Button>
          </Grid>
        </Grid>
      </div>
    </Box>
  );
};

export default ClinicForm;
